import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import {
  Button,
  Chip,
  DataTable,
  Field,
  Select,
  useToast,
  type Column,
} from '@w7/ui'
import {
  cmApi,
  type BrowseEvent,
  type BrowseLeague,
  type BrowseMarket,
  type CmSport,
} from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { CmPanel, SPORTS } from './CmPanel'
import { CatalogWorkflow } from './catalogGuide'

export function CatalogBrowsePage() {
  const toast = useToast()
  const [sport, setSport] = useState<CmSport>('soccer')
  const [flaggedOnly, setFlaggedOnly] = useState(false)
  const [leagues, setLeagues] = useState<BrowseLeague[]>([])
  const [events, setEvents] = useState<BrowseEvent[]>([])
  const [markets, setMarkets] = useState<BrowseMarket[]>([])
  const [selectedLeague, setSelectedLeague] = useState<BrowseLeague | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<BrowseEvent | null>(null)

  const loadLeagues = useMutation({
    mutationFn: () => cmApi.browseLeagues(authHeaders(), sport, { flaggedOnly, limit: 100 }),
    onSuccess: (data) => {
      setLeagues(data.leagues)
      setEvents([])
      setMarkets([])
      setSelectedLeague(null)
      setSelectedEvent(null)
      toast.success(`${data.leagues.length} league(s)`)
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const loadEvents = useMutation({
    mutationFn: (league: BrowseLeague) =>
      cmApi.browseEvents(authHeaders(), league.leagueId, { limit: 100 }),
    onSuccess: (data, league) => {
      setSelectedLeague(league)
      setEvents(data.events)
      setMarkets([])
      setSelectedEvent(null)
      toast.success(`${data.events.length} event(s) in ${league.name}`)
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const loadMarkets = useMutation({
    mutationFn: (event: BrowseEvent) =>
      cmApi.browseMarkets(authHeaders(), event.vendorEventId),
    onSuccess: (data, event) => {
      setSelectedEvent(event)
      setMarkets(data.markets)
      toast.success(`${data.markets.length} market(s)`)
    },
    onError: (err: Error) => toast.error(err.message),
  })

  useEffect(() => {
    loadLeagues.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when sport / filter changes
  }, [sport, flaggedOnly])

  const leagueCols: Column<BrowseLeague>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'League',
        cell: (row) => (
          <button type="button" className="cm-table-link" onClick={() => loadEvents.mutate(row)}>
            {row.name}
          </button>
        ),
        sortValue: (row) => row.name,
      },
      {
        id: 'flagged',
        header: 'Automation',
        width: 96,
        cell: (row) =>
          row.isCompetitionFlagged ? (
            <Chip tone="profit" size="sm">
              flagged
            </Chip>
          ) : (
            <Chip tone="neutral" size="sm">
              —
            </Chip>
          ),
      },
      {
        id: 'events',
        header: 'Events',
        width: 72,
        numeric: true,
        cell: (row) => row.eventCount,
        sortValue: (row) => row.eventCount,
      },
      {
        id: 'bf',
        header: 'Betfair id',
        cell: (row) => <code className="cm-mono">{row.vendorLeagueId}</code>,
      },
    ],
    [loadEvents],
  )

  const eventCols: Column<BrowseEvent>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Event',
        cell: (row) => (
          <button type="button" className="cm-table-link" onClick={() => loadMarkets.mutate(row)}>
            {row.name}
          </button>
        ),
        sortValue: (row) => row.name,
      },
      {
        id: 'inPlay',
        header: 'Status',
        width: 90,
        cell: (row) =>
          row.inPlay ? (
            <Chip tone="brand" size="sm">
              in-play
            </Chip>
          ) : (
            <Chip tone="neutral" size="sm">
              {row.status}
            </Chip>
          ),
      },
      {
        id: 'bf',
        header: 'Betfair id',
        cell: (row) => <code className="cm-mono">{row.vendorEventId}</code>,
      },
      {
        id: 'manage',
        header: '',
        width: 120,
        cell: (row) => (
          <Link
            className="cm-inline-link"
            to={`/catalog/market-selection?q=${encodeURIComponent(row.vendorEventId)}`}
          >
            Manage markets
          </Link>
        ),
      },
    ],
    [loadMarkets],
  )

  const marketCols: Column<BrowseMarket>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Market',
        cell: (row) => row.name,
        sortValue: (row) => row.name,
      },
      {
        id: 'type',
        header: 'Type',
        cell: (row) => row.marketType,
      },
      {
        id: 'enabled',
        header: 'Enabled',
        width: 88,
        cell: (row) => (row.enabled ? 'yes' : 'no'),
      },
      {
        id: 'id',
        header: 'Market id',
        cell: (row) => <code className="cm-mono">{row.vendorMarketId}</code>,
      },
    ],
    [],
  )

  return (
    <CmPanel
      wide
      title="Browse catalog"
      subtitle="Read-only drill-down: league → event → markets. Enable and edit limits on Market selection."
      actions={
        <Button
          type="button"
          variant="secondary"
          busy={loadLeagues.isPending}
          onClick={() => loadLeagues.mutate()}
        >
          Refresh leagues
        </Button>
      }
    >
      <div className="cm-catalog-card">
        <CatalogWorkflow
          steps={[
            {
              title: 'Pick sport',
              body: 'Leagues load automatically when you change sport. Cricket, tennis, and soccer are separate lists.',
            },
            {
              title: 'Drill down',
              body: 'Click a league, then an event, to see all market types stored on the portal.',
            },
            {
              title: 'Enable markets',
              body: (
                <>
                  Use <Link to="/catalog/market-selection">Market selection</Link> to turn on Match
                  Odds, fancy, session, and bookmaker markets.
                </>
              ),
            },
          ]}
        />

        <div className="cm-form-grid" style={{ marginTop: 16 }}>
        <Field
          label="Sport"
          required
          hint="Soccer, tennis, and cricket are managed separately."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={sport}
              onChange={(ev) => setSport(ev.target.value as CmSport)}
              options={[...SPORTS]}
            />
          )}
        </Field>
        <label className="cm-toggle">
          <input
            type="checkbox"
            checked={flaggedOnly}
            onChange={(ev) => setFlaggedOnly(ev.target.checked)}
          />
          <span>Show automation-flagged leagues only</span>
        </label>
        </div>

        <p className="cm-hint cm-browse-crumb">
          {selectedLeague ? (
            <>
              <strong>{selectedLeague.name}</strong>
              {selectedEvent ? (
                <>
                  {' '}
                  → <strong>{selectedEvent.name}</strong> (Betfair {selectedEvent.vendorEventId})
                </>
              ) : (
                ' → pick an event'
              )}
            </>
          ) : loadLeagues.isPending ? (
            'Loading leagues…'
          ) : (
            `${leagues.length} league(s) — click a league, then event, then view markets`
          )}
        </p>
      </div>

      <div className="cm-catalog-card">
      <div className="cm-browse-grid">
        <section className="cm-browse-pane" aria-label="Leagues">
          <h3 className="cm-result-section-title">1. Leagues</h3>
          <p className="cm-browse-pane-desc">
            Competitions stored on the portal. “Automation” = flagged for auto-fetch cron.
          </p>
          <DataTable
            label="Leagues"
            density="default"
            rows={leagues}
            columns={leagueCols}
            rowKey={(row) => row.leagueId}
            maxHeight={520}
            empty={<p className="cm-result-empty">{loadLeagues.isPending ? 'Loading…' : 'No leagues for this sport.'}</p>}
          />
        </section>
        <section className="cm-browse-pane" aria-label="Events">
          <h3 className="cm-result-section-title">2. Events</h3>
          <p className="cm-browse-pane-desc">
            Matches in the selected league. Manage markets opens Market selection for that event.
          </p>
          <DataTable
            label="Events"
            density="default"
            rows={events}
            columns={eventCols}
            rowKey={(row) => row.eventId}
            maxHeight={520}
            empty={<p className="cm-result-empty">Select a league first.</p>}
          />
        </section>
        <section className="cm-browse-pane" aria-label="Markets">
          <h3 className="cm-result-section-title">3. Markets</h3>
          <p className="cm-browse-pane-desc">
            Read-only list. To enable or edit limits, use Market selection.
          </p>
          <DataTable
            label="Markets"
            density="default"
            rows={markets}
            columns={marketCols}
            rowKey={(row) => row.id}
            maxHeight={520}
            empty={<p className="cm-result-empty">Select an event first.</p>}
          />
        </section>
      </div>
      </div>
    </CmPanel>
  )
}
